import { useEffect } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import {
  setIsPrimaryAnimatedSideBarOpen,
  setPrimarySideBarDataTag,
} from '../ducks/calendarActions';
import { SIDEBAR_DRAWER_BREAKPOINT } from '../constants/sidebarDimensions';

/** The icon-rail tag that renders CalendarCalendarPicker */
const DEFAULT_SIDE_BAR_TAG = 'calendar2';

/**
 * Day view starts with the sidebar open on its Calendar tab; week and month
 * view start with it collapsed, as does the phone-width drawer. This only
 * reacts to the view (or program type) changing, so a coach who closes the
 * sidebar in day view keeps it closed until they leave day view and return.
 *
 * Mount it above IconSideBar and CalendarPanel in the tree: both close or
 * clear the sidebar in their own effects, and React runs a parent's effects
 * after its children's, which is what lets this one have the final say.
 */
const useDefaultSideBarOpen = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const viewType = useSelector((state) => state.calendar.data.viewType);
  const programType = useSelector((state) => state.calendar.data.programType);
  const isTvMode = useSelector((state) => state.calendar.ui.isTvMode);

  useEffect(() => {
    /** Open state and tag are read live, not subscribed: as deps they'd
     * re-fire this effect (and re-open the sidebar) the moment the user
     * closed it. Reading from the store also sees CalendarPanel's close,
     * which lands in an earlier effect of the same commit whenever
     * programType changes - this render's closure would still say "open". */
    const {
      isPrimaryAnimatedSideBarOpen,
      primarySideBarDataTag,
    } = store.getState().calendar.data;
    const isDrawer = window.innerWidth <= SIDEBAR_DRAWER_BREAKPOINT;

    if (viewType === 'dayView') {
      /** Free-form programs have no Calendar tab on the rail. */
      if (isTvMode || isDrawer || programType === 'free-form-program' || isPrimaryAnimatedSideBarOpen) {
        return;
      }
      dispatch(setIsPrimaryAnimatedSideBarOpen(true));
      dispatch(setPrimarySideBarDataTag(DEFAULT_SIDE_BAR_TAG));
      return;
    }

    /** Leaving day view undoes the default and only the default: a tab the
     * user picked themselves still carries across views, as it always has. */
    if (isPrimaryAnimatedSideBarOpen && primarySideBarDataTag === DEFAULT_SIDE_BAR_TAG) {
      dispatch(setIsPrimaryAnimatedSideBarOpen(false));
      dispatch(setPrimarySideBarDataTag(null));
    }
  }, [viewType, programType, isTvMode, dispatch, store]);
};

export default useDefaultSideBarOpen;
